// Deployment billing periods and the "Export for invoicing" workbook (UI design §3.7). Dates are company-timezone
// calendar dates (YYYY-MM-DD); the workbook is built in the browser with exceljs, loaded only when exporting.
import type { TransferStatus } from '@fleetspoke/core/types';
import type { Adjustment, BillingRow } from '../data/billing';
import { startOfDayIso } from './time';

export type PeriodKind = 'this_month' | 'last_month' | 'to_date' | 'custom';

/** Today's calendar date in a timezone, as YYYY-MM-DD. */
export function isoDateIn(timeZone: string, at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

const pad = (n: number) => String(n).padStart(2, '0');
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate(); // m: 1-12

/** The date range of a period. `to_date` runs from the project start (or the earliest date) to today. */
export function periodRange(
  kind: Exclude<PeriodKind, 'custom'>,
  today: string,
  projectStart?: string | null,
): { from: string; to: string } {
  const [y, m] = today.split('-').map(Number) as [number, number];
  if (kind === 'this_month') return { from: `${y}-${pad(m)}-01`, to: today };
  if (kind === 'last_month') {
    const [ly, lm] = m === 1 ? [y - 1, 12] : [y, m - 1];
    return { from: `${ly}-${pad(lm)}-01`, to: `${ly}-${pad(lm)}-${pad(lastDay(ly, lm))}` };
  }
  return { from: projectStart && projectStart <= today ? projectStart : '2000-01-01', to: today };
}

// ---------------------------------------------------------------------------------------------------------------
// Asset movements on the project page: the transfers that explain why a deployment has not started yet or has
// stopped. Deployments follow custody, which moves only on RECEIPT (brief fix #6) — dated from the arrival
// sighting when there is one — so an incoming transfer bills nothing until it is received, and an outgoing one
// keeps billing until the destination receives it.
// ---------------------------------------------------------------------------------------------------------------

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A period of calendar dates as instants: [start of `from`, start of the day after `to`) in the timezone. Null
 * while a custom range is incomplete or reversed.
 */
export function periodInstants(
  range: { from: string; to: string },
  timeZone: string,
): { fromIso: string; toIso: string } | null {
  if (!ISO_DATE.test(range.from) || !ISO_DATE.test(range.to) || range.from > range.to) return null;
  const dayAfter = new Date(Date.parse(`${range.to}T00:00:00Z`) + 86_400_000)
    .toISOString()
    .slice(0, 10);
  return { fromIso: startOfDayIso(range.from, timeZone), toIso: startOfDayIso(dayAfter, timeZone) };
}

export type MovementDirection = 'in' | 'out' | 'within';
export type BillingEffect =
  'starts_on_receipt' | 'started' | 'billed_until_receipt' | 'stopped' | 'partial' | 'none';

interface MovementLike {
  status: TransferStatus;
  from_site_id: string | null;
  to_site_id: string | null;
  created_at: string;
  received_at: string | null;
  updated_at: string;
}

const OPEN_STATUSES: TransferStatus[] = ['draft', 'dispatched', 'awaiting_receipt'];

export function movementDirection(m: MovementLike, projectSites: Set<string>): MovementDirection {
  const from = !!m.from_site_id && projectSites.has(m.from_site_id);
  const to = !!m.to_site_id && projectSites.has(m.to_site_id);
  return from && to ? 'within' : to ? 'in' : 'out';
}

/** What the transfer does to this project's billing (a move between two of its sites keeps the deployment). */
export function billingEffect(m: MovementLike, direction: MovementDirection): BillingEffect {
  if (m.status === 'cancelled' || direction === 'within') return 'none';
  if (m.status === 'disputed') return 'partial'; // only the lines that were received moved
  const open = OPEN_STATUSES.includes(m.status);
  if (direction === 'in') return open ? 'starts_on_receipt' : 'started';
  return open ? 'billed_until_receipt' : 'stopped';
}

/**
 * The transfers that matter for a period [fromIso, toIso): created before it ends, and either still open or
 * closed (received / disputed / cancelled) inside or after it. Open first, then the latest closed.
 */
export function movementsInPeriod<T extends MovementLike>(
  rows: T[],
  fromIso: string,
  toIso: string,
): T[] {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  const closedAt = (m: T) => Date.parse(m.received_at ?? m.updated_at);
  const open = (m: T) => OPEN_STATUSES.includes(m.status);
  return rows
    .filter((m) => Date.parse(m.created_at) < to && (open(m) || closedAt(m) >= from))
    .sort((a, b) =>
      open(a) !== open(b)
        ? open(a)
          ? -1
          : 1
        : open(a)
          ? Date.parse(b.created_at) - Date.parse(a.created_at)
          : closedAt(b) - closedAt(a),
    );
}

/** "+2 days" / "−1 day" / "rate 12.50 → 20.00" — the change an audit line records. */
export function describeAdjustment(
  a: Adjustment,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (a.days !== undefined) {
    const sign = a.days > 0 ? '+' : '−';
    return t('billing.adjust_line_days', { count: Math.abs(a.days), sign });
  }
  const fmt = (v: number | null | undefined) => (v == null ? '—' : Number(v).toFixed(2));
  return t('billing.adjust_line_rate', { from: fmt(a.rate_from), to: fmt(a.rate_to) });
}

export interface WorkbookText {
  title: string;
  project: string;
  client: string;
  period: string;
  generated: string;
  sheet: string;
  auditSheet: string;
  total: string;
  columns: {
    project: string;
    asset: string;
    description: string;
    category: string;
    site: string;
    started: string;
    ended: string;
    expected: string;
    periodDays: string;
    adjustmentDays: string;
    days: string;
    rate: string;
    currency: string;
    amount: string;
    change: string;
    reason: string;
    by: string;
    when: string;
  };
  describe(a: Adjustment): string;
}

/**
 * Build the invoicing workbook: one sheet of deployments with a SUM total row, one of audit lines, and — when
 * given — the period's asset movements (already rendered as text by the page).
 */
export async function billingWorkbook(input: {
  rows: BillingRow[];
  projectName: string | null;
  clientName: string | null;
  from: string;
  to: string;
  timeZone: string;
  names: Map<string, string>;
  text: WorkbookText;
  movements?: { sheet: string; header: string[]; rows: string[][] };
}): Promise<Uint8Array> {
  const { default: ExcelJS } = await import('exceljs');
  const { rows, text, timeZone } = input;
  const date = (iso: string | null) => (iso ? isoDateIn(timeZone, new Date(iso)) : '');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Fleetspoke Assets';
  wb.created = new Date();

  const ws = wb.addWorksheet(text.sheet);
  ws.addRow([text.title]).font = { bold: true, size: 14 };
  ws.addRow([text.project, input.projectName ?? text.columns.project]);
  if (input.clientName) ws.addRow([text.client, input.clientName]);
  ws.addRow([text.period, `${input.from} – ${input.to}`]);
  ws.addRow([text.generated, isoDateIn(timeZone)]);
  ws.addRow([]);
  const c = text.columns;
  const header = ws.addRow([
    c.project,
    c.asset,
    c.description,
    c.category,
    c.site,
    c.started,
    c.ended,
    c.expected,
    c.periodDays,
    c.adjustmentDays,
    c.days,
    c.rate,
    c.currency,
    c.amount,
  ]);
  header.font = { bold: true };
  header.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EEF4' } };
  });
  const first = header.number + 1;
  for (const r of rows) {
    ws.addRow([
      r.project_name,
      r.asset_code,
      r.asset_description ?? '',
      r.category_name ?? '',
      r.site_name ?? '',
      date(r.started_at),
      date(r.ended_at),
      date(r.expected_return_at),
      r.period_days,
      r.adjustment_days,
      r.days_billable,
      r.rate == null ? null : Number(r.rate),
      r.currency ?? '',
      Number(r.amount),
    ]);
  }
  const last = first + rows.length - 1;
  const days = rows.reduce((s, r) => s + r.days_billable, 0);
  const amount = Math.round(rows.reduce((s, r) => s + Number(r.amount), 0) * 100) / 100;
  const currencies = [...new Set(rows.map((r) => r.currency).filter(Boolean))];
  const totalRow = ws.addRow([
    text.total,
    ...Array(9).fill(null),
    rows.length ? { formula: `SUM(K${first}:K${last})`, result: days } : 0,
    null,
    currencies.length === 1 ? currencies[0] : '',
    rows.length ? { formula: `SUM(N${first}:N${last})`, result: amount } : 0,
  ]);
  totalRow.font = { bold: true };
  for (const col of ['L', 'N']) ws.getColumn(col).numFmt = '#,##0.00';
  const widths = [24, 14, 28, 18, 22, 12, 12, 14, 10, 12, 10, 10, 9, 14];
  widths.forEach((w, i) => (ws.getColumn(i + 1).width = w));
  ws.views = [{ state: 'frozen', ySplit: header.number }];

  const audit = wb.addWorksheet(text.auditSheet);
  audit.addRow([c.asset, c.change, c.reason, c.by, c.when]).font = { bold: true };
  for (const r of rows) {
    for (const a of r.adjustments ?? []) {
      audit.addRow([
        r.asset_code,
        text.describe(a),
        a.reason ?? '',
        (a.by && input.names.get(a.by)) || '',
        a.at ? date(a.at) : '',
      ]);
    }
  }
  [14, 22, 40, 22, 12].forEach((w, i) => (audit.getColumn(i + 1).width = w));

  if (input.movements) {
    const moves = wb.addWorksheet(input.movements.sheet);
    moves.addRow(input.movements.header).font = { bold: true };
    for (const r of input.movements.rows) moves.addRow(r);
    input.movements.header.forEach((_, i) => (moves.getColumn(i + 1).width = i === 4 ? 40 : 18));
    moves.views = [{ state: 'frozen', ySplit: 1 }];
  }

  return new Uint8Array(await wb.xlsx.writeBuffer());
}

export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** "billing_Bukit-Jalil-tower_2026-09-01_2026-09-30.xlsx" */
export function billingFileName(projectName: string | null, from: string, to: string): string {
  const slug = (projectName ?? 'all-projects')
    .normalize('NFKD')
    .replace(/[^\w-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return `billing_${slug || 'project'}_${from}_${to}.xlsx`;
}
